import { useState, useMemo } from 'react';
import { BOMDropdownGroup, BOMDropdownVariant } from '../../../../api/item.api';
import { formatINR } from '@/utils/format';

interface AddComponentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (item: BOMDropdownGroup | BOMDropdownVariant) => void;
  excludeItemIds?: string[];
  items: BOMDropdownGroup[];
}

export default function AddComponentModal({
  isOpen,
  onClose,
  onSelect,
  excludeItemIds = [],
  items,
}: AddComponentModalProps) {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredItems = useMemo(() => {
    const q = searchQuery.toLowerCase();

    if (!q) return items;

    return items.filter((group) => {
      const matchesItem =
        group.name.toLowerCase().includes(q) ||
        (group.code && group.code.toLowerCase().includes(q));

      const matchesVariant = group.variants.some(
        (v) =>
          v.name.toLowerCase().includes(q) ||
          (v.code && v.code.toLowerCase().includes(q)),
      );

      return matchesItem || matchesVariant;
    });
  }, [items, searchQuery]);

  // Order: 1. Semi Finished -> 2. Raw Material -> 3. Other (Consumable, Packaging, etc.)
  const semiFinishedItems = useMemo(() => {
    return filteredItems.filter((group) => {
      const rawType = String((group as any).item_type || group.category || '').toUpperCase().replace(/[\s-]/g, '_');
      return rawType.includes('SEMI');
    });
  }, [filteredItems]);

  const rawMaterialItems = useMemo(() => {
    return filteredItems.filter((group) => {
      const rawType = String((group as any).item_type || group.category || '').toUpperCase().replace(/[\s-]/g, '_');
      return rawType.includes('RAW') && !rawType.includes('SEMI');
    });
  }, [filteredItems]);

  const otherItems = useMemo(() => {
    return filteredItems.filter((group) => {
      const rawType = String((group as any).item_type || group.category || '').toUpperCase().replace(/[\s-]/g, '_');
      const isSemi = rawType.includes('SEMI');
      const isRaw = rawType.includes('RAW');
      return !isSemi && !isRaw;
    });
  }, [filteredItems]);

  const getItemTypeBadge = (itemTypeStr: string) => {
    const t = String(itemTypeStr || '').toUpperCase().replace(/[\s-]/g, '_');
    if (t.includes('SEMI')) return { label: 'Semi Finished', cls: 'bg-amber-50 text-amber-700 border-amber-200' };
    if (t.includes('RAW')) return { label: 'Raw Material', cls: 'bg-orange-50 text-orange-700 border-orange-200' };
    if (t.includes('CONSUMABLE')) return { label: 'Consumable', cls: 'bg-blue-50 text-blue-700 border-blue-200' };
    if (t.includes('PACKAGING')) return { label: 'Packaging', cls: 'bg-slate-50 text-slate-700 border-slate-200' };
    return { label: itemTypeStr || 'Component', cls: 'bg-gray-50 text-gray-700 border-gray-200' };
  };

  const renderGroupRow = (group: BOMDropdownGroup) => {
    const isExcluded = excludeItemIds.includes(group.id);
    const itemType = (group as any).item_type || group.category || '';
    const badge = getItemTypeBadge(itemType);

    return (
      <div key={group.id} className="mb-2">
        {/* Parent Item */}
        <button
          onClick={() => onSelect(group)}
          disabled={isExcluded}
          className={`w-full px-3 py-2 text-left rounded-lg transition-colors flex items-center justify-between ${
            isExcluded
              ? 'opacity-50 cursor-not-allowed bg-slate-50'
              : 'hover:bg-indigo-50/80 cursor-pointer'
          }`}
        >
          <div className="min-w-0 pr-2">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-sm font-semibold text-[#1e293b] truncate">
                {group.name}
              </p>
              <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full border ${badge.cls}`}>
                {badge.label}
              </span>
            </div>
            <p className="text-xs text-slate-400 font-mono mt-0.5">{group.code}</p>
          </div>
          <div className="text-right shrink-0">
            <p className="text-sm font-medium text-[#1e293b]">
              {formatINR(group.purchase_rate)}
            </p>
            <p className="text-xs text-slate-400">{group.unit_name}</p>
          </div>
        </button>

        {/* Variants (indented) */}
        {group.variants.length > 0 && (
          <div className="ml-6 border-l border-slate-200 pl-2 mt-1 space-y-1">
            {group.variants.map((variant) => {
              const isVarExcluded = excludeItemIds.includes(variant.id);
              const varType = (variant as any).item_type || itemType;
              const varBadge = getItemTypeBadge(varType);

              return (
                <button
                  key={variant.id}
                  onClick={() => onSelect(variant)}
                  disabled={isVarExcluded}
                  className={`w-full px-3 py-1.5 text-left rounded-lg transition-colors flex items-center justify-between ${
                    isVarExcluded
                      ? 'opacity-50 cursor-not-allowed bg-slate-50'
                      : 'hover:bg-indigo-50/80 cursor-pointer'
                  }`}
                >
                  <div className="flex items-center gap-2 min-w-0 pr-2">
                    <i className="ri-subtract-line text-xs text-slate-400 shrink-0" />
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <p className="text-sm text-[#475569] font-medium truncate">
                          {variant.name}
                        </p>
                        <span className={`text-[9px] font-medium px-1.5 py-0.2 rounded-full border ${varBadge.cls}`}>
                          {varBadge.label}
                        </span>
                      </div>
                      <p className="text-xs text-slate-400 font-mono mt-0.5">
                        {variant.code}
                      </p>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-sm font-medium text-[#1e293b]">
                      {formatINR(variant.purchase_rate)}
                    </p>
                    <p className="text-xs text-slate-400">
                      {variant.unit_name}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-xl w-[600px] max-w-full max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-[#e2e8f0]">
          <h3 className="text-lg font-semibold text-[#1e293b]">
            Add Component
          </h3>
          <p className="text-sm text-slate-500 mt-0.5">
            Select a semi-finished item, raw material, or consumable to add as component
          </p>
        </div>

        {/* Search */}
        <div className="p-4 border-b border-[#e2e8f0]">
          <input
            type="text"
            autoFocus
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search components or variants..."
            className="w-full h-10 px-3 text-sm bg-slate-50 border border-[#e2e8f0] rounded-lg focus:outline-none focus:border-[#4f46e5]"
          />
        </div>

        {/* Items List - Grouped with Type Headers */}
        <div className="flex-1 overflow-y-auto p-2">
          {filteredItems.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              <i className="ri-inbox-line text-3xl mb-2 block" />
              <p className="text-sm">No items found</p>
            </div>
          ) : (
            <div>
              {/* 1. Semi-Finished Goods Section */}
              {semiFinishedItems.length > 0 && (
                <div className="mb-4">
                  <div className="sticky top-0 z-10 px-3 py-1.5 bg-amber-50/95 backdrop-blur-xs border-y border-amber-200 flex items-center justify-between text-xs font-semibold text-amber-800 rounded-md mb-1.5">
                    <div className="flex items-center gap-1.5">
                      <i className="ri-settings-4-fill text-amber-600 text-sm" />
                      <span>Semi-Finished Goods ({semiFinishedItems.length})</span>
                    </div>
                    <span className="text-[10px] font-bold text-amber-700 bg-amber-200/80 px-1.5 py-0.5 rounded uppercase">
                      Semi Finished
                    </span>
                  </div>
                  <div>{semiFinishedItems.map(renderGroupRow)}</div>
                </div>
              )}

              {/* 2. Raw Material Section */}
              {rawMaterialItems.length > 0 && (
                <div className="mb-4">
                  <div className="sticky top-0 z-10 px-3 py-1.5 bg-orange-50/95 backdrop-blur-xs border-y border-orange-200 flex items-center justify-between text-xs font-semibold text-orange-800 rounded-md mb-1.5">
                    <div className="flex items-center gap-1.5">
                      <i className="ri-inbox-archive-line text-orange-600 text-sm" />
                      <span>Raw Materials ({rawMaterialItems.length})</span>
                    </div>
                    <span className="text-[10px] font-bold text-orange-700 bg-orange-200/80 px-1.5 py-0.5 rounded uppercase">
                      Raw Material
                    </span>
                  </div>
                  <div>{rawMaterialItems.map(renderGroupRow)}</div>
                </div>
              )}

              {/* 3. Other Goods (Consumables, Packaging, etc.) */}
              {otherItems.length > 0 && (
                <div className="mb-4">
                  <div className="sticky top-0 z-10 px-3 py-1.5 bg-blue-50/95 backdrop-blur-xs border-y border-blue-200 flex items-center justify-between text-xs font-semibold text-blue-800 rounded-md mb-1.5">
                    <div className="flex items-center gap-1.5">
                      <i className="ri-apps-line text-blue-600 text-sm" />
                      <span>Other Components ({otherItems.length})</span>
                    </div>
                    <span className="text-[10px] font-bold text-blue-700 bg-blue-200/80 px-1.5 py-0.5 rounded uppercase">
                      Other
                    </span>
                  </div>
                  <div>{otherItems.map(renderGroupRow)}</div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-[#e2e8f0] flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-[#64748b] hover:bg-[#f1f5f9] rounded-lg cursor-pointer"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
